/**
 * Mixin reutilizable para acciones masivas con progreso visual
 * Uso en un componente:
 *
 * import batchActionsMixin from '@/assets/mixins/batchActionsMixin'
 * export default {
 *   mixins: [batchActionsMixin],
 *   methods: {
 *     async markAllInvoicesPaid() {
 *       const ids = this.selectedInvoiceIds;
 *       await this.executeBatchAction({
 *         title: "Marcando como pagadas",
 *         items: ids,
 *         processor: (batch) => invoiceService.invoicePaidBatch(batch),
 *         onSuccess: () => this.loadInvoices()
 *       });
 *     }
 *   }
 * }
 */

import {
  processBatch,
  createProgressToast
} from "@/assets/utils/batchProcessor";

export default {
  data() {
    return {
      batchProcessing: false,
      batchProgress: {
        current: 0,
        total: 0
      }
    };
  },

  methods: {
    /**
     * Ejecutar una acción masiva con feedback visual
     * @param {Object} config
     *   - title: string - Título del progreso
     *   - items: Array - Items a procesar
     *   - processor: Function(batch) => Promise - Función que procesa cada lote
     *   - onProgress: Function(current, total) - Callback de progreso (opcional)
     *   - onSuccess: Function(results) - Callback al terminar exitosamente (opcional)
     *   - onError: Function(error) - Callback si hay error (opcional)
     *   - batchSize: number - Items por request (default: 5)
     *   - stopOnError: boolean - Parar en primer error (default: false)
     */
    async executeBatchAction(config) {
      const {
        title,
        items,
        processor,
        onProgress,
        onSuccess,
        onError,
        batchSize = 5,
        stopOnError = false
      } = config;

      if (!items || items.length === 0) {
        this.$notifications.warning({
          message: "No hay elementos para procesar"
        });
        return;
      }

      this.batchProcessing = true;
      const toast = createProgressToast(this.$notifications, title);

      try {
        // Mostrar toast de progreso
        toast.show(items.length);

        // Procesar en lotes
        const results = await processBatch(items, processor, {
          batchSize,
          stopOnError,
          onProgress: (processed, total) => {
            this.batchProgress = { current: processed, total };
            toast.update(processed);
            if (onProgress) onProgress(processed, total);
          },
          onError: (error, batch, processed) => {
            if (onError) onError(error, batch, processed);
          }
        });

        // Mostrar resumen final
        const errorCount = results.errors.length;
        const successCount = results.success;

        if (errorCount > 0) {
          toast.error(`${successCount} éxito, ${errorCount} errores`);
        } else {
          toast.success(successCount);
        }

        // Callback de éxito
        if (onSuccess) onSuccess(results);

        return results;
      } catch (error) {
        toast.error("Error en la operación masiva");
        throw error;
      } finally {
        this.batchProcessing = false;
        this.batchProgress = { current: 0, total: 0 };
      }
    },

    /**
     * Helper: Marcar múltiples facturas como pagadas
     */
    async markInvoicesPaid(invoiceIds, onSuccess) {
      const invoiceService = require("@/modules/dashboard/sales/pages/invoices/services/invoiceService")
        .default;

      return this.executeBatchAction({
        title: "Marcando facturas como pagadas",
        items: invoiceIds,
        processor: batch => invoiceService.invoicePaidBatch(batch, true),
        onSuccess,
        batchSize: 5
      });
    },

    /**
     * Helper: Cambiar estado de múltiples facturas
     */
    async updateInvoicesStatus(invoiceIds, statusData, onSuccess) {
      const invoiceService = require("@/modules/dashboard/sales/pages/invoices/services/invoiceService")
        .default;

      return this.executeBatchAction({
        title: `Actualizando estado (${invoiceIds.length} documentos)`,
        items: invoiceIds,
        processor: batch =>
          invoiceService.updateInvoiceStatusBatch(batch, statusData),
        onSuccess,
        batchSize: 5
      });
    }
  }
};
